import { useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Box } from '@cbhq/cds-web/layout/Box';
import { Pagination } from '@cbhq/cds-web/pagination/Pagination';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@cbhq/cds-web/tables';
import { Text } from '@cbhq/cds-web/typography/Text';

import type { Column } from '../types';

import type { UserCellMember } from './UserCell';
import { UserCell } from './UserCell';

const DEFAULT_ITEMS_PER_PAGE = 10;
const tableStyle: React.CSSProperties = { width: '100%', minWidth: 0 };
const TEAM_TABLE_MIN_HEIGHT = 780;
const containerStyle: React.CSSProperties = { flex: 1, minHeight: TEAM_TABLE_MIN_HEIGHT } as const;
const paginationBoxStyle: React.CSSProperties = {
  marginTop: 'auto',
  marginBottom: 36,
};

const messages = defineMessages({
  colName: {
    id: 'teamTable.colName',
    defaultMessage: 'Name',
    description: 'Column header for member name',
  },
  colEntity: {
    id: 'teamTable.colEntity',
    defaultMessage: 'Entity',
    description: 'Column header for entity',
  },
  colProduct: {
    id: 'teamTable.colProduct',
    defaultMessage: 'Product',
    description: 'Column header for product (org tab)',
  },
  colRole: {
    id: 'teamTable.colRole',
    defaultMessage: 'Role',
    description: 'Column header for role (product tab)',
  },
  colStatus: {
    id: 'teamTable.colStatus',
    defaultMessage: 'Status',
    description: 'Column header for member status',
  },
  colLastOnline: {
    id: 'teamTable.colLastOnline',
    defaultMessage: 'Last online',
    description: 'Column header for last online date',
  },
  previousPage: {
    id: 'teamTable.previousPage',
    defaultMessage: 'Previous page',
    description: 'Accessibility label for previous page button',
  },
  nextPage: {
    id: 'teamTable.nextPage',
    defaultMessage: 'Next page',
    description: 'Accessibility label for next page button',
  },
  tableName: {
    id: 'teamTable.tableName',
    defaultMessage: 'Team members',
    description: 'Accessible label for the team members table',
  },
});

const tableCellWhitespaceStyle = { paddingY: 0, paddingX: 3 } as const;
const firstTableCellWhitespaceStyle = { paddingY: 0, paddingX: 0, paddingStart: 2 } as const;
const lastTableCellWhitespaceStyle = { paddingY: 0, paddingEnd: 3, paddingStart: 2 } as const;

export type TeamTableProps = {
  /** Pre-filtered, pre-sorted members to display */
  members: UserCellMember[];
  orgName: string;
  columns: Column[];
  productName?: string;
  paginationResetKey?: string;
  /** Emails of members whose rows are animating out (opacity → 0) */
  removingMemberEmails?: Set<string>;
  itemsPerPage?: number;
  onNameClick: (member: UserCellMember) => void;
  onRemoveFromOrg: (member: UserCellMember) => void;
  onCancelInvite?: (member: UserCellMember) => void;
  onEditRole?: (member: UserCellMember, productName: string) => void;
  showActions?: boolean;
  /** Whether the current user has permission to remove members from the org */
  canRemove?: boolean;
  /** Whether the current user has permission to edit member access */
  canEdit?: boolean;
};

export function TeamTable({
  members,
  orgName,
  columns,
  productName,
  paginationResetKey,
  removingMemberEmails,
  itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
  onNameClick,
  onRemoveFromOrg,
  onCancelInvite,
  onEditRole,
  showActions = true,
  canRemove = false,
  canEdit = false,
}: TeamTableProps) {
  const { formatMessage } = useIntl();
  const [currentPage, setCurrentPage] = useState(1);
  const previousResetKeyRef = useRef(paginationResetKey);
  const totalPages = Math.max(1, Math.ceil(members.length / itemsPerPage));
  const activePage = Math.min(currentPage, totalPages);

  const accessibilityLabels = useMemo(
    () => ({
      previous: formatMessage(messages.previousPage),
      next: formatMessage(messages.nextPage),
    }),
    [formatMessage],
  );

  // Reset pagination for filter/search changes. Otherwise keep the current page
  // unless the current page no longer exists after the list size changes.
  useEffect(() => {
    const shouldReset = previousResetKeyRef.current !== paginationResetKey;
    previousResetKeyRef.current = paginationResetKey;

    if (shouldReset) {
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, paginationResetKey, totalPages]);

  const paginatedMembers = useMemo(
    () => members.slice((activePage - 1) * itemsPerPage, activePage * itemsPerPage),
    [activePage, itemsPerPage, members],
  );

  const headerStrings: Record<Column, string> = {
    name: formatMessage(messages.colName),
    entity: formatMessage(messages.colEntity),
    product: formatMessage(messages.colProduct),
    role: formatMessage(messages.colRole),
    status: formatMessage(messages.colStatus),
    lastOnline: formatMessage(messages.colLastOnline),
  };

  return (
    <Box
      flexDirection="column"
      paddingX={0}
      width="100%"
      style={containerStyle}
      data-testid="team-members-table"
    >
      <Table
        accessibilityLabel={formatMessage(messages.tableName)}
        tableLayout="fixed"
        style={tableStyle}
      >
        <TableHeader>
          <TableRow disableHoverIndicator>
            {columns.map((col, i) => (
              <TableCell
                key={col}
                as="th"
                scope="col"
                outerSpacing={i === 0 ? firstTableCellWhitespaceStyle : tableCellWhitespaceStyle}
              >
                <Text font="label1" color="fgMuted">
                  {headerStrings[col]}
                </Text>
              </TableCell>
            ))}
            {showActions && (
              <TableCell as="th" width={64} title="" outerSpacing={lastTableCellWhitespaceStyle} />
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedMembers.map((member) => (
            <UserCell
              key={member.email}
              member={member}
              orgName={orgName}
              columns={columns}
              productName={productName}
              isRemoving={removingMemberEmails?.has(member.email) ?? false}
              onNameClick={onNameClick}
              onRemoveFromOrg={onRemoveFromOrg}
              onCancelInvite={onCancelInvite}
              onEditRole={onEditRole}
              showActions={showActions}
              canRemove={canRemove}
              canEdit={canEdit}
            />
          ))}
        </TableBody>
      </Table>

      <Box justifyContent="center" style={paginationBoxStyle}>
        <Pagination
          totalPages={totalPages}
          activePage={activePage}
          onChange={setCurrentPage}
          accessibilityLabels={accessibilityLabels}
          width={450}
          justifyContent="space-between"
        />
      </Box>
    </Box>
  );
}
